import {
    useCallback, useEffect, useRef, MouseEvent as MouseEventReact, RefObject,
} from 'react';

import {
    IScrollState,
    createScrollState,
    updateScrollbarStyles,
    calculateScrollFromMousePosition,
    createRAFUpdater,
} from '../lib';
import {SCROLL_HEIGHT_THRESHOLD} from '../model/constants';

interface IUseHorizontalScrollParams {
  containerRef: RefObject<HTMLElement>;
  scrollableSelector?: string;
  enabled?: boolean;
}

/**
 * Хук для управления горизонтальной прокруткой с кастомным скроллбаром
 *
 * Предоставляет функциональность для создания кастомного горизонтального скроллбара
 * с поддержкой RAF-оптимизированного обновления и пороговой логики отображения.
 *
 * @param params - Параметры конфигурации хука
 * @param params.containerRef - Ссылка на контейнер, внутри которого нужно найти прокручиваемый элемент
 * @param params.scrollableSelector - CSS селектор для поиска прокручиваемого элемента (по умолчанию пустая строка)
 * @param params.enabled - Включен ли хук (по умолчанию true)
 *
 * @returns Объект с состоянием скролла, обработчиками событий и рефами для скроллбара
 *
 */
export const useHorizontalScroll = ({
    containerRef,
    scrollableSelector = '',
    enabled = true,
}: IUseHorizontalScrollParams) => {
    const progressRef = useRef<HTMLDivElement>(null);
    const thumbRef = useRef<HTMLDivElement>(null);
    const scrollbarRef = useRef<HTMLDivElement>(null);
    const scrollableElementRef = useRef<HTMLElement | null>(null);
    const scrollStateRef = useRef<IScrollState>({
        scrollLeft: 0,
        maxScrollLeft: 0,
        percentage: 0,
        isScrollable: false,
    });
    const rafUpdater = useRef(createRAFUpdater());

    const updateScrollableElementRef = useCallback(() => {
        if (!containerRef.current || !containerRef.current?.querySelector) return null;

        const scrollableElement = containerRef.current.querySelector(scrollableSelector) as HTMLElement | null;

        if (!scrollableElement) return null;

        scrollableElementRef.current = scrollableElement;
        return scrollableElementRef.current;
    }, [containerRef, scrollableSelector]);

    const updateThumbPosition = useCallback((percentage: number) => {
        rafUpdater.current.update(() => {
            updateScrollbarStyles(progressRef.current, thumbRef.current, percentage);
        });
    }, []);

    const setScrollPosition = useCallback((newScrollLeft: number) => {
        if (!enabled || !scrollableElementRef.current || !scrollStateRef.current.isScrollable) return;

        const clampedScrollLeft = Math.max(0, Math.min(newScrollLeft, scrollStateRef.current.maxScrollLeft));
        scrollableElementRef.current.scrollLeft = clampedScrollLeft;

        const newState = createScrollState(
            clampedScrollLeft,
            scrollableElementRef.current.scrollWidth,
            scrollableElementRef.current.clientWidth,
        );

        scrollStateRef.current = newState;
        updateThumbPosition(newState.percentage);
    }, [enabled, updateThumbPosition]);

    const updateScrollState = useCallback(() => {
        if (!enabled || !scrollableElementRef.current) return;

        const newState = createScrollState(
            scrollableElementRef.current.scrollLeft,
            scrollableElementRef.current.scrollWidth,
            scrollableElementRef.current.clientWidth,
        );

        scrollStateRef.current = newState;

        if (newState.isScrollable) {
            updateThumbPosition(newState.percentage);
        }
    }, [enabled, updateThumbPosition, updateScrollableElementRef, scrollableElementRef.current]);

    useEffect(() => {
        if (!scrollableElementRef.current) {
            updateScrollableElementRef();
        }

        if (!enabled || !scrollableElementRef.current) return undefined;

        updateScrollState();

        const hasScroll = scrollableElementRef.current.scrollWidth > scrollableElementRef.current.clientWidth;

        if (!hasScroll) return undefined;

        scrollableElementRef.current.addEventListener('scroll', updateScrollState, {passive: true});
        return () => scrollableElementRef.current?.removeEventListener('scroll', updateScrollState);
    }, [enabled, updateScrollState, updateScrollableElementRef, scrollableElementRef.current]);

    const handleMouseDown = useCallback((e: MouseEventReact) => {
        e.preventDefault();

        const track = e.currentTarget;

        if (!scrollStateRef.current.isScrollable || !track) return;

        const rect = track.getBoundingClientRect();

        const handleMouseMove = (moveEvent: MouseEvent) => {
            moveEvent.preventDefault();
            const newScrollLeft = calculateScrollFromMousePosition(
                moveEvent.clientX,
                rect,
                scrollStateRef.current.maxScrollLeft,
            );
            setScrollPosition(newScrollLeft);
        };

        const handleMouseUp = () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };

        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);

        handleMouseMove(e.nativeEvent);
    }, [setScrollPosition]);

    useEffect(() => () => {
        rafUpdater.current.cancel();
    }, []);

    const containerHeight = scrollableElementRef?.current?.scrollHeight;
    const isThresholdReached = typeof containerHeight === 'number' && containerHeight > SCROLL_HEIGHT_THRESHOLD;

    return {
        /** Текущее состояние прокрутки с позицией, процентом и флагом доступности скролла */
        scrollState: scrollStateRef.current,
        /** Обработчик нажатия мыши для взаимодействия со скроллбаром */
        handleMouseDown,
        /** Реф для элемента прогресса (заполненная часть скроллбара) */
        progressRef,
        /** Реф для элемента thumb (ползунок скроллбара) */
        thumbRef,
        /** Реф для контейнера скроллбара */
        scrollbarRef,
        /** Флаг, указывающий, достигнут ли пороговый размер для отображения скроллбара */
        isThresholdReached,
    };
};
